import { useState, useEffect, useCallback } from 'react';
import { PayCalculatorProps } from './index';

export interface PayPeriodDetail {
  start: string;
  end: string;
  label: string;
  hours: number;
  estimatedPay: number;
}

export const useHooks = ({
  dayMap,
  rateType,
  dailyRate,
  hourlyRate,
  onUpdateRates,
  passcode
}: PayCalculatorProps) => {
  // Initialize start/end dates to current pay cycle (semi-monthly: 1-15 / 16-end)
  const getInitialDates = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const day = now.getDate();

    if (day <= 15) {
      return { start: `${yyyy}-${mm}-01`, end: `${yyyy}-${mm}-15` };
    } else {
      const lastDay = new Date(yyyy, now.getMonth() + 1, 0).getDate();
      const dd = String(lastDay).padStart(2, '0');
      return { start: `${yyyy}-${mm}-16`, end: `${yyyy}-${mm}-${dd}` };
    }
  };

  const initialDates = getInitialDates();
  const [payStart, setPayStart] = useState(initialDates.start);
  const [payEnd, setPayEnd] = useState(initialDates.end);
  const [isPrivateMode, setIsPrivateMode] = useState(true);

  // Pagination states for standard pay cycles
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [payPeriods, setPayPeriods] = useState<PayPeriodDetail[]>([]);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);

  const fetchPayPeriods = useCallback(async () => {
    if (!passcode) return;
    setIsLoadingPeriods(true);
    try {
      const res = await fetch(`/api/pay-periods?page=${page}&limit=3`, {
        headers: {
          'x-passcode': passcode
        }
      });
      if (res.ok) {
        const body = await res.json();
        setPayPeriods(body.data || []);
        setTotalPages(Math.ceil((body.total || 0) / (body.perPage || 3)));
      }
    } catch (err) {
      console.error('Failed to fetch pay periods:', err);
    } finally {
      setIsLoadingPeriods(false);
    }
  }, [page, passcode]);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      if (active) {
        fetchPayPeriods();
      }
    }, 0);
    
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [fetchPayPeriods, dayMap]);

  const handleRateTypeChange = (type: 'daily' | 'hourly') => {
    onUpdateRates(type, dailyRate, hourlyRate);
  };

  const handleDailyRateChange = (val: string) => {
    onUpdateRates(rateType, val, hourlyRate);
  };

  const handleHourlyRateChange = (val: string) => {
    onUpdateRates(rateType, dailyRate, val);
  };

  const dailyRateNumber = parseFloat(dailyRate) || 0;
  const hourlyRateNumber = parseFloat(hourlyRate) || 0;
  
  let calcTotalHours = 0;
  Object.entries(dayMap).forEach(([dateStr, hours]) => {
    if (dateStr >= payStart && dateStr <= payEnd) {
      calcTotalHours += hours;
    }
  });

  const estimatedPay = rateType === 'daily'
    ? (calcTotalHours / 8) * dailyRateNumber
    : calcTotalHours * hourlyRateNumber;

  const formatPeso = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return {
    payStart,
    setPayStart,
    payEnd,
    setPayEnd,
    isPrivateMode,
    setIsPrivateMode,
    calcTotalHours,
    estimatedPay,
    formatPeso,
    handleRateTypeChange,
    handleDailyRateChange,
    handleHourlyRateChange,
    payPeriods,
    page,
    setPage,
    totalPages,
    isLoadingPeriods
  };
};
